import { ReactNode } from 'react';
import { useTranslation } from '../../i18n';
import facilityFlowLogo from '../../assets/facilityflow-logo.svg';
import facilityFlowLogoWhite from '../../assets/facilityflow-logo-white.svg';

interface AuthLayoutProps {
  children: ReactNode;
}

/**
 * Shared two-column shell for the standalone auth pages (forgot/reset password):
 * branded green panel on the left, centered form card on the right.
 */
export function AuthLayout({ children }: AuthLayoutProps) {
  const { t } = useTranslation(['auth']);

  return (
    <div className="min-h-screen bg-paper lg:grid lg:grid-cols-[1.1fr_1fr] xl:grid-cols-[1.25fr_1fr]">
      {/* Left: brand panel */}
      <aside
        className="hidden lg:flex relative flex-col justify-between p-10 xl:p-14 text-white overflow-hidden"
        style={{ background: '#00A871' }}
      >
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'repeating-linear-gradient(135deg, transparent 0 14px, rgba(255,255,255,0.06) 14px 28px)',
          }}
        />

        <div className="relative">
          <img src={facilityFlowLogoWhite} alt="FacilityFlow" className="h-14 w-auto" />
        </div>

        <div className="relative max-w-md space-y-8">
          <div>
            <div className="font-mono-num text-[10px] uppercase tracking-[0.22em] text-emerald-100/80 mb-3">
              FacilityFlow Suite
            </div>
            <h1 className="text-[40px] xl:text-[48px] font-semibold leading-[1.05] tracking-tight">
              {t('auth:login.tagline')}
            </h1>
          </div>
        </div>

        <div className="relative font-mono-num text-[10px] uppercase tracking-[0.22em] text-emerald-100/70">
          {`v${__APP_VERSION__}`}
        </div>
      </aside>

      {/* Right: form panel */}
      <main className="flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-6">
            <img
              src={facilityFlowLogo}
              alt="FacilityFlow"
              className="h-16 w-auto mx-auto mb-2"
            />
            <p className="text-[12px] text-slate-500 mt-0.5">{t('auth:login.tagline')}</p>
          </div>

          {children}

          <p className="lg:hidden text-center font-mono-num text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-6">
            {`v${__APP_VERSION__}`}
          </p>
        </div>
      </main>
    </div>
  );
}
